// Criar / editar um marco, sem sair da linha do tempo.
//
// Mesmo padrão do mapa e do calendário: o formulário abre na própria página,
// por cima da lista, e fecha quando termina. Antes isto vivia numa página à
// parte (/adicionar, com abas) e outra só para editar (/marco/:id/editar) —
// duas viagens para uma coisa que se faz a olhar para o trilho.

import { useState } from "react";
import { CATEGORY_LIST as LISTA_CATEGORIAS } from "@historia/shared/categories";
import { todayIso } from "@historia/shared/dates";
import type { Milestone as Marco } from "@historia/shared/types";
import { AFTER_MILESTONE_CHANGE, createMilestone, updateMilestone } from "@/api/mutations";
import { useAccao } from "@/api/useAccao";

const campo =
  "rounded-xl border border-ouro/30 bg-creme-50 px-3 py-2 text-sm text-tinta outline-none " +
  "placeholder:text-tinta-700/40 focus:border-ouro focus:ring-2 focus:ring-ouro/30";
const etiqueta = "text-xs font-semibold text-tinta-700";

export function FormMarco({
  marco,
  aoTerminar,
}: {
  /** Preenchido = editar; ausente = criar. */
  marco?: Marco | null;
  aoTerminar: () => void;
}) {
  const aEditar = Boolean(marco);

  const [titulo, setTitulo] = useState(marco?.titulo ?? "");
  const [data, setData] = useState(marco?.data ?? todayIso());
  const [categoria, setCategoria] = useState<string>(marco?.categoria ?? "primeiros");
  const [local, setLocal] = useState(marco?.local ?? "");
  const [descricao, setDescricao] = useState(marco?.descricao ?? "");

  const [estado, guardar, aGuardar] = useAccao(
    aEditar ? updateMilestone : createMilestone,
    { invalidate: AFTER_MILESTONE_CHANGE, onSuccess: aoTerminar },
  );

  async function submeter(evento: React.FormEvent) {
    evento.preventDefault();
    await guardar({
      // `id` só é lido pelo updateMilestone; no createMilestone fica pelo caminho.
      id: marco?.id ?? "",
      titulo,
      data,
      categoria,
      local: local || null,
      descricao: descricao || null,
    } as never);
  }

  return (
    <form
      onSubmit={submeter}
      className="flex flex-col gap-3 rounded-2xl border border-ouro/25 bg-creme-50 p-4 text-left"
    >
      <h3 className="font-display text-lg text-tinta">
        {aEditar ? "Editar marco" : "Novo marco"}
      </h3>

      <label className="flex flex-col gap-1">
        <span className={etiqueta}>Título</span>
        <input
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          required
          placeholder="O nosso primeiro encontro"
          className={campo}
        />
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1">
          <span className={etiqueta}>Data</span>
          <input
            type="date"
            value={data}
            onChange={(e) => setData(e.target.value)}
            required
            className={campo}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className={etiqueta}>Categoria</span>
          <select
            value={categoria}
            onChange={(e) => setCategoria(e.target.value)}
            required
            className={campo}
          >
            {LISTA_CATEGORIAS.map((c) => (
              <option key={c.key} value={c.key}>
                {c.icon} {c.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="flex flex-col gap-1">
        <span className={etiqueta}>Local (opcional)</span>
        <input
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          placeholder="Lisboa"
          className={campo}
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className={etiqueta}>Descrição (opcional)</span>
        <textarea
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
          rows={4}
          placeholder="Conta como foi…"
          className={`${campo} resize-y`}
        />
      </label>

      {estado.erro && (
        <p
          role="alert"
          className="rounded-2xl border border-blush/60 bg-blush-300/30 px-4 py-2.5 text-sm text-tinta"
        >
          {estado.erro}
        </p>
      )}

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={aoTerminar}
          className="rounded-full px-4 py-1.5 text-sm font-semibold text-tinta-700 transition-colors hover:bg-creme-200"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={aGuardar}
          className="rounded-full bg-ouro px-5 py-1.5 text-sm font-semibold text-creme-50 transition-colors hover:bg-ouro-600 disabled:opacity-60"
        >
          {aGuardar ? "A guardar…" : "Guardar"}
        </button>
      </div>
    </form>
  );
}
